import { useState } from "react";
import { ChevronLeft, ChevronRight, RotateCcw, Download, Copy, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";

interface Flashcard {
  id: number;
  question: string;
  answer: string;
  difficulty: 'easy' | 'medium' | 'hard';
  category?: string;
}

interface FlashcardMetadata {
  topic: string;
  total_cards: number;
  estimated_study_time: string;
}

interface FlashcardViewerProps {
  flashcards: Flashcard[];
  metadata: FlashcardMetadata;
  userPrompt: string;
}

export const FlashcardViewer = ({ flashcards, metadata, userPrompt }: FlashcardViewerProps) => {
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const { toast } = useToast();

  const currentCard = flashcards[currentCardIndex];

  const nextCard = () => {
    setCurrentCardIndex((prev) => (prev + 1) % flashcards.length);
    setShowAnswer(false);
  };

  const prevCard = () => {
    setCurrentCardIndex((prev) => (prev - 1 + flashcards.length) % flashcards.length);
    setShowAnswer(false);
  };

  const resetCards = () => {
    setCurrentCardIndex(0);
    setShowAnswer(false);
  };

  const copyFlashcards = () => {
    const text = flashcards.map((card, index) => 
      `Card ${index + 1}:\nQ: ${card.question}\nA: ${card.answer}\nDifficulty: ${card.difficulty}\n${card.category ? `Category: ${card.category}\n` : ''}`
    ).join('\n---\n\n');
    
    const fullText = `Flashcards: ${metadata.topic}\nGenerated from: ${userPrompt}\nTotal Cards: ${metadata.total_cards}\nEstimated Study Time: ${metadata.estimated_study_time}\n\n${text}`;
    
    navigator.clipboard.writeText(fullText);
    toast({
      title: "Copied",
      description: "All flashcards copied to clipboard"
    });
  };

  const downloadFlashcards = () => {
    const text = flashcards.map((card, index) => 
      `Card ${index + 1}:\nQ: ${card.question}\nA: ${card.answer}\nDifficulty: ${card.difficulty}\n${card.category ? `Category: ${card.category}\n` : ''}`
    ).join('\n---\n\n');
    
    const fullText = `Flashcards: ${metadata.topic}\nGenerated from: ${userPrompt}\nTotal Cards: ${metadata.total_cards}\nEstimated Study Time: ${metadata.estimated_study_time}\n\n${text}`;
    
    const blob = new Blob([fullText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flashcards-${metadata.topic.toLowerCase().replace(/\s+/g, '-')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast({
      title: "Downloaded",
      description: "Flashcards saved to your downloads"
    });
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'hard': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!flashcards || flashcards.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-slate-500">No flashcards to display</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 mb-2">{metadata.topic}</h2>
        <div className="flex flex-wrap gap-2 text-sm text-slate-600">
          <span>{metadata.total_cards} cards</span>
          <span>•</span>
          <span>{metadata.estimated_study_time}</span>
          {currentCard.category && (
            <>
              <span>•</span>
              <span>{currentCard.category}</span>
            </>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">
            {currentCardIndex + 1} of {flashcards.length}
          </span>
          <Badge className={getDifficultyColor(currentCard.difficulty)}>
            {currentCard.difficulty}
          </Badge>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={resetCards}
            className="gap-1"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={copyFlashcards}
            className="gap-1"
          >
            <Copy className="w-4 h-4" />
            Copy All
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={downloadFlashcards}
            className="gap-1"
          >
            <Download className="w-4 h-4" />
            Download
          </Button>
        </div>
      </div>

      {/* Flashcard */}
      <div className="relative">
        <div className="bg-white border-2 border-slate-200 rounded-xl p-6 min-h-[300px] shadow-sm">
          <div className="flex flex-col h-full">
            {/* Question */}
            <div className="mb-4">
              <div className="text-sm text-slate-500 mb-2">Question</div>
              <div className="text-lg text-slate-900 leading-relaxed">
                {currentCard.question}
              </div>
            </div>

            {/* Answer */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div className="text-sm text-slate-500">Answer</div>
                <button
                  onClick={() => setShowAnswer(!showAnswer)}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showAnswer ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className={`text-slate-900 leading-relaxed transition-opacity duration-200 ${
                showAnswer ? 'opacity-100' : 'opacity-30 blur-sm'
              }`}>
                {currentCard.answer}
              </div>
            </div>

            {/* Show Answer Button */}
            {!showAnswer && (
              <div className="mt-4 text-center">
                <Button
                  onClick={() => setShowAnswer(true)}
                  variant="outline"
                  className="gap-2"
                >
                  <Eye className="w-4 h-4" />
                  Show Answer
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center mt-4">
          <Button
            variant="outline"
            onClick={prevCard}
            disabled={flashcards.length <= 1}
            className="gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </Button>
          
          <div className="flex gap-1">
            {flashcards.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setCurrentCardIndex(index);
                  setShowAnswer(false);
                }}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentCardIndex ? 'bg-purple-600' : 'bg-slate-300'
                }`}
              />
            ))}
          </div>
          
          <Button
            variant="outline"
            onClick={nextCard}
            disabled={flashcards.length <= 1}
            className="gap-2"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};